import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AddDoor} from 'assets';
import {getValidStructure} from 'hooks/QFP/Shared';
import {useAppSelector} from 'store/customer';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {shallowEqual} from 'react-redux';
import styled, {css} from 'styled-components';
import {touchDevices} from 'shared/Breakpoints';
import {StaticSizeImage} from 'shared/components/StaticSizeImage';
import AdvancedMaterials from 'components/customer/AdvancedMaterials/AdvancedMaterials';
import {useMaterialType} from 'components/customer/Product/Specs/helpers/useMaterialType';
import {useAutoButtonClick} from 'components/customer/Product/Specs/helpers/useAutoButtonClick';
import {Page} from 'store/customer/entity/Page';

interface DoorStyleProps {
    isExterior: boolean;
    productInfo?: Partial<ReturnType<typeof getValidStructure>>;
    isMiniBrowser?: boolean;
    page?: Page;
}

export const DoorStyle = ({
    isExterior,
    isMiniBrowser,
    productInfo,
    page = Page.PRODUCT,
}: DoorStyleProps) => {
    const {materialType, cabinetType} = useMaterialType(isExterior);
    const {buttonRef, handleClick} = useAutoButtonClick();

    const [styleError, setStyleError] = useState(false);
    const door = useAppSelector(getDoor, shallowEqual);

    const handleStyleImageError = useCallback(() => {
        setStyleError(true);
    }, []);

    useEffect(() => {
        if (door?.image?.name) {
            setStyleError(false);
        }
    }, [door]);

    const {styleTitle, buttonText} = useMemo(() => {
        let styleTitle;
        let buttonText;

        if (productInfo) {
            if (productInfo.hasDoors && !productInfo.hasDrawers) {
                styleTitle = 'Door Style';
                buttonText = 'Change Door';
            } else if (!productInfo.hasDoors && productInfo.hasDrawers) {
                styleTitle = 'Drawer Style';
                buttonText = 'Change Drawer';
            } else if (productInfo.hasDoors && productInfo.hasDrawers) {
                styleTitle = 'Door and Drawer Style';
                buttonText = 'Change Style';
            }
        }

        return {styleTitle, buttonText};
    }, [productInfo]);

    return (
        <DoorStyleContainer $isMiniBrowser={isMiniBrowser}>
            <Label $isMiniBrowser={isMiniBrowser}>{styleTitle}</Label>
            <LocalStaticSizeImage
                inlineSVG={styleError}
                image={styleError ? AddDoor : door?.image?.name || ''}
                width={140}
                height={140}
                onError={handleStyleImageError}
                onClick={handleClick}
            />
            <ProductAdvancedMaterials
                buttonRef={buttonRef}
                materialType={materialType}
                cabinetType={cabinetType}
                hasDoors={
                    (isExterior &&
                        (productInfo?.hasDoors || productInfo?.hasDrawers)) ||
                    false
                }
                buttonText={buttonText}
                $minWidth={140}
                page={page}
            />
        </DoorStyleContainer>
    );
};

const LocalStaticSizeImage = styled(StaticSizeImage)`
    cursor: pointer;
`;

export const Label = styled.div<{$isMiniBrowser?: boolean}>`
    color: ${({theme}) => theme?.colors?.primary?.main};
    font-weight: 500;
    text-align: left;
    font-size: 0.75em;

    ${({$isMiniBrowser}) => $isMiniBrowser && `width: 100%;`}

    ${touchDevices`
        width: 100%;
    `}
`;

const MobileDoorStyleContainer = css`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    width: 45%;
    flex-direction: column;

    > button {
        width: 50% !important;
    }
`;

const DoorStyleContainer = styled.div<{$isMiniBrowser?: boolean}>`
    display: flex;
    flex-direction: column;

    .static-image {
        margin-bottom: 15px;
    }

    ${({$isMiniBrowser}) => $isMiniBrowser && MobileDoorStyleContainer}

    ${touchDevices`
        ${MobileDoorStyleContainer}
    `}
`;

export const ProductAdvancedMaterials = styled(AdvancedMaterials)<{
    $minWidth?: number;
}>`
    min-width: ${({$minWidth}) => ($minWidth ? `${$minWidth}px` : '150px')};
`;
